import { useState, useEffect, type FC } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import 'dayjs/locale/fr';
import { Calendar } from 'lucide-react';
import { GuidedTour } from '@/components/onboarding/GuidedTour';
import { usePageTour } from '@/hooks/usePageTour';
import { contentCalendarTourSteps } from '@/lib/tour-data';
import CalendarToolbar from './components/CalendarToolbar';
import CalendarGrid from './components/CalendarGrid';
import CreateEntryModal from './components/CreateEntryModal';
import type { CalendarEntryData } from './components/CalendarEntry';

dayjs.locale('fr');

const TOUR_ID = 'admin-content-calendar';

const ContentCalendarPage: FC = () => {
  const tour = usePageTour(TOUR_ID);
  useEffect(() => {
    const handler = () => tour.startTour();
    window.addEventListener('restart-page-tour', handler);
    return () => window.removeEventListener('restart-page-tour', handler);
  }, [tour]);

  const [currentMonth, setCurrentMonth] = useState<Dayjs>(dayjs());
  const [selectedEntry, setSelectedEntry] = useState<CalendarEntryData | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [viewMode, setViewMode] = useState<'month' | 'week'>('month');

  const handleAdd = () => {
    setSelectedEntry(null);
    setSelectedDate(null);
    setCreateModalOpen(true);
  };

  const handleEntryClick = (entry: CalendarEntryData) => {
    setSelectedEntry(entry);
    setCreateModalOpen(true);
  };

  const handleDayClick = (date: Dayjs) => {
    setSelectedDate(date);
    setSelectedEntry(null);
    setCreateModalOpen(true);
  };

  const handleClose = () => {
    setCreateModalOpen(false);
    setSelectedEntry(null);
    setSelectedDate(null);
  };

  return (
    <div className="space-y-6">
      {/* Header gradient */}
      <div
        className="fc-gradient-header rounded-2xl p-6 text-white"
        style={{ background: 'linear-gradient(135deg, #3b82f6, #6366f1)' }}
      >
        <div className="flex items-center gap-3" data-tour="content-calendar-header">
          <Calendar className="h-6 w-6" />
          <div>
            <h1 className="text-xl font-bold">Content Calendar</h1>
            <p className="mt-0.5 text-xs italic text-blue-200">Calendrier editorial</p>
            <p className="mt-1 text-sm text-blue-100">
              Planification editoriale — Publications &amp; Campagnes
            </p>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <CalendarToolbar
        currentMonth={currentMonth}
        onPrev={() => setCurrentMonth((m) => m.subtract(1, 'month'))}
        onNext={() => setCurrentMonth((m) => m.add(1, 'month'))}
        onToday={() => setCurrentMonth(dayjs())}
        onAdd={handleAdd}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
      />

      {/* Grille calendrier */}
      <CalendarGrid
        month={currentMonth}
        onEntryClick={handleEntryClick}
        onDayClick={handleDayClick}
        viewMode={viewMode}
      />

      {/* Modal creation / edition */}
      <CreateEntryModal
        open={createModalOpen || !!selectedEntry}
        entry={selectedEntry}
        defaultDate={selectedDate}
        onClose={handleClose}
      />
      <GuidedTour tourId={TOUR_ID} steps={contentCalendarTourSteps} isActive={tour.isActive} onComplete={tour.completeTour} onSkip={tour.skipTour} />
    </div>
  );
};

export default ContentCalendarPage;
